import { ContainerItem } from '../../../models/logs/ContainerItem';

/**
 * container-items.rules.ts
 *
 * Pure rules for the repeatable (quantity, type) container list the Details
 * step captures and the submit sends as `containerItems`.
 *
 * A delivery can be "3 box + 2 pallet". The backend numbers labels within each
 * type (BOX 1 of 3 … PALLET 2 of 2), so the ONE thing the UI must not allow is
 * the same type twice: "3 box, 2 box" would produce two stickers both captioned
 * "BOX 1", and the backend rejects it with a 400. Everything here exists to keep
 * that (and the other 400s) from ever reaching the network.
 */

/** Maximum length of a container type, matching the backend column/validator. */
export const MAX_ITEM_TYPE_LENGTH = 50;

/**
 * One row of the Details step's container list — the editable working model.
 * `itemType` is blank until the user picks a chip or types a custom noun.
 */
export interface ContainerRow {
  /** Number of physical units of this type. Clamped to ≥ 1 before submit. */
  quantity: number;
  /** The unit noun as the user entered it (e.g. "Box", "cylinders"). */
  itemType: string;
}

/** Why a container row can't be submitted. `null` in the errors array = the row is fine. */
export type ContainerRowError = 'blank' | 'duplicate' | 'tooLong' | 'quantity';

/**
 * Normalizes a container type for comparison and submission: trimmed,
 * lower-cased, whitespace collapsed, and singularized.
 *
 * Two types are "the same" when their normalized values match, and matching is
 * what stops a delivery carrying the same containers twice. Singularizing is
 * part of that, not cosmetics: a user who has already tapped "Box" and then
 * types "boxes" into the custom field means the same containers, and letting
 * those through as two types would put "BOX 1 of 3" and "BOXES 1 of 2" on
 * physically identical boxes — the exact outcome the no-duplicates rule exists
 * to prevent, and one the backend can't catch either (the strings differ).
 * It also keeps labels reading "BOX 1 of 3" rather than "BOXES 1 of 3".
 */
export function normalizeItemType(raw: string | null | undefined): string {
  const noun = (raw ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
  if (!noun) { return ''; }

  const words = noun.split(' ');
  words[words.length - 1] = singularizeUnitNoun(words[words.length - 1]);
  return words.join(' ');
}

/**
 * Conservatively singularizes a unit word: "-xes/-ches/-shes/-zes" drop "es"
 * ("boxes" → "box"), other "-s" (not "-ss") drops the "s" ("crates" → "crate",
 * "pallets" → "pallet"). Anything else is returned unchanged.
 */
export function singularizeUnitNoun(word: string): string {
  if (/(?:x|ch|sh|z)es$/.test(word)) { return word.slice(0, -2); }
  if (word.length > 1 && word.endsWith('s') && !word.endsWith('ss')) { return word.slice(0, -1); }
  return word;
}

/**
 * Per-row validation errors, in row order. Only the SECOND (and later)
 * occurrence of a type is flagged `duplicate`, so the error points at the row
 * the user just added rather than at the one they entered first.
 *
 * @param rows - The Details step's container rows.
 * @returns One entry per row: the first error found, or null when the row is valid.
 */
export function validateContainerRows(rows: ContainerRow[]): (ContainerRowError | null)[] {
  const seen = new Set<string>();
  return (rows ?? []).map((row) => {
    const type = normalizeItemType(row.itemType);
    if (!type) { return 'blank'; }
    if (type.length > MAX_ITEM_TYPE_LENGTH) { return 'tooLong'; }
    if (seen.has(type)) { return 'duplicate'; }
    seen.add(type);
    if (!Number.isFinite(row.quantity) || Math.floor(row.quantity) < 1) { return 'quantity'; }
    return null;
  });
}

/** True when every row is valid and there is at least one — the submit gate. */
export function areContainerRowsValid(rows: ContainerRow[]): boolean {
  if (!rows?.length) { return false; }
  return validateContainerRows(rows).every((e) => e === null);
}

/**
 * Maps the Details step's rows onto the `containerItems` the submit sends:
 * normalized type, whole-number quantity of at least 1. Blank rows are dropped
 * so a half-filled row can never reach the API (the step blocks on them anyway).
 */
export function toContainerItems(rows: ContainerRow[]): ContainerItem[] {
  return (rows ?? [])
    .map((row) => ({
      quantity: Math.max(1, Math.floor(Number(row.quantity) || 0)),
      itemType: normalizeItemType(row.itemType),
    }))
    .filter((item) => item.itemType.length > 0);
}

/**
 * Composes the human quantity string — "3 BOX, 2 PALLET".
 *
 * PREVIEW ONLY. The persisted value always comes from the backend, which derives
 * it from `containerItems` and overwrites whatever the request carried; after a
 * submit, read `quantityDisplay` off the response instead of calling this.
 */
export function composeQuantityDisplay(rows: ContainerRow[]): string {
  return toContainerItems(rows)
    .map((item) => `${item.quantity} ${item.itemType.toUpperCase()}`)
    .join(', ');
}

/** Total number of physical items across every type — how many labels the log prints. */
export function containerItemCount(rows: ContainerRow[]): number {
  return toContainerItems(rows).reduce((sum, item) => sum + item.quantity, 0);
}

/**
 * Expands the rows into one entry per physical item, in the order the backend
 * creates them (all of the first pair, then the second, …), each carrying the
 * caption facts that pair implies.
 *
 * Used ONLY to label the pre-submit per-item print-quantity rows on Review,
 * where no item rows exist yet. Once the submit returns `receivingItems`, that
 * response is the source of the captions — its `typeIndex`/`typeTotal` are
 * stored facts, and these are a guess at what they will be.
 */
export function previewItemCaptions(rows: ContainerRow[]): { itemType: string; typeIndex: number; typeTotal: number }[] {
  return toContainerItems(rows).flatMap((item) =>
    Array.from({ length: item.quantity }, (_, i) => ({
      itemType: item.itemType,
      typeIndex: i + 1,
      typeTotal: item.quantity,
    })),
  );
}

/**
 * The caption printed on an item's label and shown on its print/reprint row:
 * "BOX 2 of 3", or just "BOX" when it's the only one of its type (a lone
 * "BOX 1 of 1" reads like something is missing).
 */
export function itemCaption(item: { itemType: string; typeIndex: number; typeTotal: number }): string {
  const noun = (item.itemType || 'item').toUpperCase();
  return item.typeTotal > 1 ? `${noun} ${item.typeIndex} of ${item.typeTotal}` : noun;
}

/**
 * Variant of {@link itemCaption} that always states the count, even for a lone
 * item of its type ("TUBE 1 of 1"). Used ONLY by the QR-carrying copier sheet
 * (`PrintService.usesQrSheet`), whose caption sits beside a QR code rather than
 * next to other lines that already say how many there are — there, dropping the
 * "1 of 1" reads as missing information rather than as the redundancy it is
 * everywhere else {@link itemCaption} is used.
 */
export function itemCaptionAlwaysCounted(item: { itemType: string; typeIndex: number; typeTotal: number }): string {
  const noun = (item.itemType || 'item').toUpperCase();
  return `${noun} ${item.typeIndex} of ${item.typeTotal}`;
}
